import {
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import styles from "../styles";

interface Props {
  campaignName?: string;
  onBack: () => void;
  onRename?: (newName: string) => Promise<void>;
  hideClose?: boolean;
  creditsLeft?: number | null;
}

export default function Header({
  campaignName,
  onBack,
  onRename,
  hideClose,
  creditsLeft,
}: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState(campaignName ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) setDraftName(campaignName ?? "");
  }, [campaignName, isEditing]);

  const trimmed = draftName.trim();
  const canSave = !saving && trimmed.length > 0 && trimmed !== campaignName;

  const handleSave = async () => {
    if (!onRename || !canSave) return;
    try {
      setSaving(true);
      await onRename(trimmed);
      setIsEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={[styles.headerRow, hideClose ? { pr: 6 } : {}]}>
      {isEditing && campaignName && onRename ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1 }}>
          <TextField
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canSave) handleSave();
              if (e.key === "Escape") setIsEditing(false);
            }}
            size="small"
            autoFocus
            disabled={saving}
            inputProps={{ maxLength: 200 }}
            sx={{
              flex: 1,
              maxWidth: 480,
              "& .MuiInputBase-input": { fontSize: 20, fontWeight: 700 },
            }}
          />
          <IconButton
            onClick={handleSave}
            disabled={!canSave}
            size="small"
            sx={{ color: "primary.main" }}
            aria-label="Save name"
          >
            {saving ? (
              <CircularProgress size={16} />
            ) : (
              <Box
                component="i"
                className="fa-regular fa-check"
                sx={{ fontSize: 16 }}
              />
            )}
          </IconButton>
          <IconButton
            onClick={() => setIsEditing(false)}
            disabled={saving}
            size="small"
            sx={{ color: "text.secondary" }}
            aria-label="Cancel rename"
          >
            <Box
              component="i"
              className="fa-regular fa-xmark"
              sx={{ fontSize: 16 }}
            />
          </IconButton>
        </Box>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={styles.title}>
            {campaignName ? `Campaign: ${campaignName}` : "Create New Campaign"}
          </Typography>
          {campaignName && onRename && (
            <Tooltip title="Rename campaign">
              <IconButton
                onClick={() => setIsEditing(true)}
                size="small"
                sx={{ color: "text.secondary" }}
                aria-label="Rename campaign"
              >
                <Box
                  component="i"
                  className="fa-regular fa-pen"
                  sx={{ fontSize: 13 }}
                />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1.5 }}>
        {creditsLeft != null && (
          <Box sx={styles.creditChip}>
            <Box component="i" className="fa-solid fa-coins" />
            {creditsLeft.toLocaleString()} credits left
          </Box>
        )}
        {!hideClose && (
          <Tooltip title="Close">
            <IconButton
              onClick={onBack}
              sx={styles.backButton}
              aria-label="Close"
            >
              <Box
                component="i"
                className="fa-regular fa-xmark"
                sx={{ fontSize: 16 }}
              />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}
